/* ⚠️ GENERATED from app/services/likeLedger.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   ❤️ services/likeLedger.ts — likes (and shares) that a viral post can hold.

   🟥 WHY. A like was `likes: arrayUnion(uid)` on the post document: every
      liker's uid inside the post, for ever. A Firestore document is capped at
      1 MiB and a uid is ~30 bytes, so a post could hold roughly 30,000 likes
      and then NOTHING more could be written to it — no like, no comment
      counter, no report, no edit. Long before that, every feed read
      downloaded every liker of every post (a 10k-like post is ~300 KB of uids
      per read), and every like was a write to one hot document that every
      viewer is listening to.

   🟢 NOW (written by the server, server/functions/likeRoutes.js):
        posts/{id}/likes/{uid}   — one document per like, the truth, unbounded
        posts/{id}.likesCount    — the running total (buffered writes)
        posts/{id}.likes         — a LEDGER of the first LIKE_LEDGER_CAP likers,
                                   kept because the hearts, the floating faces
                                   and the "liked by" list of an ordinary post
                                   all read it and it costs nothing at that size
        posts/{id}.likesV2       — set on the first like through the server
   🟢 PAST THE CAP, "did I like this?" cannot be read off the post any more, so
      this client asks the server once per post (in batches) and remembers —
      `createMyLikes` below. Below the cap nothing changed.

   🟢 SHARES GET THE SAME LEDGER: `shares` / `shareCounts` stop growing at the
      cap; `sharesCount` carries on.

   One rule for the phone, the browser and the server (tools/gen-twins.mjs).
   ═══════════════════════════════════════════════════════════════════════════ */

/** Likers kept inside the post document. ~15 KB — nowhere near 1 MiB. */
export const LIKE_LEDGER_CAP = 500;
/** Sharers kept inside the post document (shares + shareCounts). */
export const SHARE_LEDGER_CAP = 500;
/** Post ids asked about in one "did I like these?" call. */
export const MY_LIKES_BATCH = 50;
/** How long a like pressed on THIS device outranks the post's own copy. */
export const MY_LIKE_FRESH_MS = 8000;
/** Likers per page of the "liked by" sheet. */
export const LIKERS_PAGE = 50;
export function likeArray(post) {
  return Array.isArray(post && post.likes) ? post.likes.filter(u => typeof u === 'string' && !!u) : [];
}

/** Does the post keep its likes as documents (through the server)? */
export function isPagedLikes(post) {
  return !!post && post.likesV2 === true;
}

/** The ledger is full: the post can no longer say who liked it. */
export function ledgerFull(post) {
  return likeArray(post).length >= LIKE_LEDGER_CAP;
}

/**
 * How many likes a post has.
 *  · an old post: its array, as before (a counter only if there is no array)
 *  · a paged post: the server's counter — the array is only the first 500
 */
export function likeTotalOf(post) {
  const arr = likeArray(post);
  const stored = Math.max(0, Number(post && post.likesCount) || 0);
  if (isPagedLikes(post)) return Math.max(arr.length, stored);
  if (Array.isArray(post && post.likes)) return arr.length;
  return stored;
}

/** The number a screen shows right after this device toggled a like. */
export function likeTotalAfter(post, liked) {
  const was = likeTotalOf(post);
  return Math.max(0, was + (liked ? 1 : -1));
}

/** The ledger as this device should hold it right after a toggle. */
export function ledgerAfter(post, uid, liked) {
  const arr = likeArray(post).filter(u => u !== uid);
  if (!liked) return arr;
  /* past the cap the server does not add me — but THIS device may, so every
     heart on this phone that reads the array agrees with the one I pressed */
  return [...arr, uid];
}

/** What a share may add to the post besides `sharesCount` (the ledger part). */
export function shareLedgerRoom(post, uid) {
  const arr = Array.isArray(post && post.shares) ? post.shares : [];
  if (arr.includes(uid)) return true; /* already listed — their own count may grow */
  return arr.length < SHARE_LEDGER_CAP;
}

/* ═══ "DID I LIKE THIS?" — ONE ANSWER FOR THE WHOLE CLIENT ════════════════
   🟢 Below the cap the post's array answers, exactly as before.
   🟢 Past the cap the server is asked once (posts in batches of 50) and the
      answer is remembered for the session.
   🟢 A like pressed here wins for MY_LIKE_FRESH_MS everywhere — the reel, the
      feed card, the open post — before any snapshot has caught up. */

export function createMyLikes(fetcher, opts) {
  const now = opts && opts.now || (() => Date.now());
  const delayMs = opts && typeof opts.delayMs === 'number' ? opts.delayMs : 120;
  const maxKnown = opts && opts.maxKnown || 3000;
  let me = '';
  const known = new Map();
  const asking = new Set();
  const queue = new Set();
  const subs = new Set();
  let timer = null;
  let version = 0;
  const emit = () => {
    version += 1;
    for (const f of Array.from(subs)) {
      try {
        f();
      } catch (e) {/* theirs */}
    }
  };
  const remember = (id, liked, mine) => {
    known.delete(id);
    known.set(id, {
      liked,
      at: now(),
      mine
    });
    while (known.size > maxKnown) {
      const k = known.keys().next().value;
      if (k === undefined) break;
      known.delete(k);
    }
  };
  async function flush() {
    timer = null;
    const ids = Array.from(queue).slice(0, MY_LIKES_BATCH);
    for (const id of ids) {
      queue.delete(id);
      asking.add(id);
    }
    if (queue.size && !timer) timer = setTimeout(() => {
      flush();
    }, delayMs);
    if (!ids.length || !fetcher) {
      for (const id of ids) asking.delete(id);
      return;
    }
    let res = null;
    try {
      res = await fetcher(ids);
    } catch (e) {
      res = null;
    }
    for (const id of ids) asking.delete(id);
    if (!res || !res.ok) return; /* not answered: asked again next time it is drawn */
    const yes = new Set((res.liked || []).map(String));
    let changed = false;
    for (const id of ids) {
      const cur = known.get(id);
      if (cur && cur.mine) continue; /* a press on this device is newer than the question */
      if (!cur || cur.liked !== yes.has(id)) changed = true;
      remember(id, yes.has(id), false);
    }
    if (changed) emit();
  }
  return {
    setMe(uid) {
      const u = String(uid || '');
      if (u !== me) {
        me = u;
        known.clear();
        queue.clear();
        emit();
      }
    },
    me: () => me,
    get(postId) {
      const k = known.get(String(postId || ''));
      return k ? k.liked : undefined;
    },
    /** This device pressed the heart. */
    press(postId, liked) {
      const id = String(postId || '');
      if (!id) return;
      remember(id, liked, true);
      emit();
    },
    /** The server took (or refused) the press — the press is no longer "fresh". */
    settle(postId, liked) {
      const id = String(postId || '');
      if (!id) return;
      remember(id, liked, false);
      emit();
    },
    /** Is the heart full, for this user on this post. */
    likedBy(post, uid) {
      const u = String(uid || me || '');
      if (!post || !u) return false;
      const id = String(post.id || '');
      const k = id ? known.get(id) : undefined;
      if (k && (ledgerFull(post) || k.mine && now() - k.at < MY_LIKE_FRESH_MS)) return k.liked;
      return likeArray(post).includes(u);
    },
    /** Drawn on screen: if the post cannot answer for itself, ask (batched). */
    want(post) {
      const id = String(post && post.id || '');
      if (!id || !me || !ledgerFull(post) || known.has(id) || asking.has(id) || queue.has(id)) return;
      queue.add(id);
      if (!timer) timer = setTimeout(() => {
        flush();
      }, delayMs);
    },
    subscribe(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    version: () => version,
    _flush: () => flush(),
    _reset() {
      known.clear();
      queue.clear();
      asking.clear();
      me = '';
      emit();
    }
  };
}
